import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { BASE_PATHS, template } from '@belong/common';
import { BulletList, ButtonBase, Image, LinkBase, Text } from '@belong/ui';
import clsx from 'clsx';
import { kebabCase } from 'es-toolkit/compat';
import { useRegions } from 'src/features/app';
import { CoverageCity } from '../../homeowners.types';
import { COVERAGE_MAP_AREAS } from './constants';
import styles from './coverage-map.module.css';
import { Dropdown } from './dropdown/dropdown';
import { COVERAGE_MAP_STRINGS as STRINGS } from './strings';

type Props = { coverageCities: CoverageCity[] };

type RegionOption = { name?: string; region?: string; all?: boolean };

export function CoverageMap({ coverageCities }: Props) {
  const router = useRouter();
  const { regions } = useRegions();
  const [selectedOption, setSelectedOption] = useState(0);
  const itWasMounted = useRef(false);

  const allRegions: RegionOption[] = [
    {
      name: 'all-regions',
      region: 'All Regions',
      all: true,
    },
    ...regions.sort((a, b) => a.region?.localeCompare(b.region || '') || 0),
  ];
  const selectedRegion = regions.find(
    (region) => region.name === allRegions[selectedOption]?.name,
  );
  const allSelected = allRegions[selectedOption]?.all;
  const pathname = `${BASE_PATHS.HOMEOWNER}/${selectedRegion?.nameKebabCase}`;

  const selectedCities = allSelected
    ? []
    : coverageCities.filter(
        (rl) => rl.regionSlug === selectedRegion?.nameKebabCase,
      );

  useEffect(() => {
    if (regions.length && !itWasMounted.current) {
      const initialSelectedOption = regions.findIndex(
        (region) =>
          router.asPath === `${BASE_PATHS.HOMEOWNER}/${region?.nameKebabCase}`,
      );

      setSelectedOption(initialSelectedOption + 1);
      itWasMounted.current = true;
    }
  }, [regions]);

  useEffect(() => {
    itWasMounted.current = false;
  }, [router.asPath]);

  const getImageConfig = (name: string) => ({
    mobile: {
      src: `/coverage_areas/v2/belong_coverage_area_map_${
        name === 'all-regions'
          ? 'all_v19_mobile.svg'
          : `${kebabCase(name ?? '')}.jpg`
      }`,
    },
    desktop: {
      src: `/coverage_areas/v2/belong_coverage_area_map_${
        name === 'all-regions' ? 'all_v19.svg' : `${kebabCase(name ?? '')}.jpg`
      }`,
    },
  });

  return (
    <div className="w-full">
      <div className="border-gray flex w-full flex-col items-center rounded border-0 border-solid lg:border">
        <div className="mb-xl border-gray lg:mb-xl relative w-full border-0 border-solid lg:border-b">
          {selectedRegion &&
            router.asPath !== pathname &&
            (selectedRegion.status === 'Active' ? (
              <LinkBase
                className="bottom-lg right-lg absolute z-10 hidden lg:block"
                href={pathname}
              >
                <div className="bg-navy p-sm duration-hover hover:bg-dark-navy rounded transition">
                  <Text className="text-white" fontWeight="semibold">
                    {template(STRINGS['explore.label'], {
                      region: selectedRegion?.region,
                    })}
                  </Text>
                </div>
              </LinkBase>
            ) : selectedRegion.status === 'ComingSoon' ? (
              <LinkBase
                className="bottom-lg right-lg absolute z-10 hidden lg:block"
                href={pathname}
              >
                <div className="bg-navy px-sm py-xs duration-hover hover:bg-dark-navy flex rounded transition">
                  <div className="mr-sm flex items-center">
                    <Image
                      src="/icons/icon_wip.svg"
                      alt={STRINGS['region_under_construction.label']}
                      className="h-md w-auto"
                    />
                  </div>
                  <div className="text-left">
                    <Text className="text-white" variant="p1">
                      {STRINGS['region_under_construction.label']}
                    </Text>
                    <Text className="text-white" fontWeight="semibold">
                      {STRINGS['learn_more.label']}
                    </Text>
                  </div>
                </div>
              </LinkBase>
            ) : null)}
          <div className="lg:aspect-h-1 lg:aspect-w-2">
            {allRegions.map(({ name }, idx) => {
              const imageConfig = getImageConfig(name ?? '');

              return (
                <div
                  key={idx}
                  className={clsx({ hidden: idx !== selectedOption })}
                >
                  <div className="block lg:hidden">
                    <Image
                      src={imageConfig.mobile.src}
                      alt="Coverage Area"
                      className="pointer-events-none h-auto"
                      useMap="#coverage_area_map"
                    />
                  </div>

                  <div className="hidden lg:block">
                    <Image
                      src={imageConfig.desktop.src}
                      alt="Coverage Area"
                      className="h-auto rounded-t"
                      useMap="#coverage_area_map"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {allSelected ? (
            <map name="coverage_area_map">
              {COVERAGE_MAP_AREAS.map((area, index) => (
                <area
                  key={index}
                  alt={area.region}
                  title={area.region}
                  href={`${BASE_PATHS.HOMEOWNER}/${area.region}`}
                  coords={area.coords}
                  shape={area.shape}
                />
              ))}
            </map>
          ) : null}
        </div>
        {allSelected ? (
          <map name="coverage_area_map">
            {COVERAGE_MAP_AREAS.map((area, index) => (
              <area
                key={index}
                alt={area.region}
                title={area.region}
                href={`${BASE_PATHS.HOMEOWNER}/${area.region}`}
                coords={area.coords}
                shape={area.shape}
              />
            ))}
          </map>
        ) : null}
        <div className="contents lg:hidden">
          {/* TODO: replace this with final Dropwdown implementation */}
          <Dropdown
            items={allRegions.map((region) => region.region)}
            onSelectedItemChange={(idx) => setSelectedOption(idx)}
            defaultSelectedItem={selectedOption}
          />
        </div>
        <div
          className={clsx('mt-xs lg:px-sm hidden lg:block lg:w-full', {
            'mb-xl': !selectedCities?.length,
          })}
        >
          <ul className={clsx(styles.regionsList, 'gap-2xs grid list-none')}>
            {allRegions.map((region, idx) => (
              <li key={idx}>
                <ButtonBase
                  onClick={() => setSelectedOption(idx)}
                  className="max-w-full text-left"
                >
                  <div
                    className={clsx(
                      styles.regionButton,
                      'px-xs rounded',
                      idx === selectedOption ? 'bg-navy' : 'bg-white',
                    )}
                  >
                    <Text
                      variant="p1"
                      fontWeight="semibold"
                      className={clsx(
                        'leading-p2 duration-hover hover:text-dark-navy truncate transition',
                        {
                          'text-white hover:text-white': idx === selectedOption,
                        },
                      )}
                    >
                      {region.region}
                    </Text>
                  </div>
                </ButtonBase>
              </li>
            ))}
          </ul>
        </div>
        {selectedCities?.length > 0 && (
          <div className="pt-xl lg:px-sm lg:pb-lg lg:pt-2xl lg:w-full">
            <div className="mb-xs">
              <Text fontWeight="semibold">
                {STRINGS['title.start']} {selectedRegion?.region}{' '}
                {STRINGS['title.end']}
              </Text>
            </div>
            <BulletList
              wrap
              size="large"
              items={selectedCities.map((city) => {
                return (
                  <LinkBase href={city.href} key={city.href}>
                    <Text
                      as="span"
                      variant="p1"
                      fontWeight="semibold"
                      className="duration-hover hover:text-dark-navy transition"
                    >
                      {city.displayName}
                    </Text>
                  </LinkBase>
                );
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
